import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { volverTrasGuardar } from "../../utils/volverTrasGuardar";

// Tipos que puede crear/editar cada rol. Debe reflejar ROL_TIPOS_VISIBLES del
// backend (adminGestionUsuarios.py); el backend es quien realmente lo aplica,
// esto solo evita ofrecer opciones que luego el servidor rechazaria.
const TIPOS_PERMITIDOS_POR_ROL = {
  admin_compras:    ["comprador", "repartidor"],
  admin_servicios:  ["supervisora", "asistenta"],
  admin_asistentas: ["supervisora", "asistenta"],
  admin_atcliente:  ["supervisora"],
  superadmin:       null, // null = todos
};

const TODOS_LOS_TIPOS = [
  { value: "superadmin", label: "Super Admin", emoji: "🛡️" },
  { value: "admin_compras", label: "Admin Compras", emoji: "🛒" },
  { value: "admin_servicios", label: "Admin Servicios", emoji: "🏠" },
  { value: "admin_asistentas", label: "Admin Asistentas", emoji: "🧙‍♀️" },
  { value: "admin_atcliente", label: "Admin At.Cliente", emoji: "🤝" },
  { value: "supervisora", label: "Supervisora", emoji: "👩‍💼" },
  { value: "comprador", label: "Comprador", emoji: "🛍️" },
  { value: "repartidor", label: "Repartidor", emoji: "🚚" },
  { value: "asistenta", label: "Asistenta", emoji: "🧹" },
  { value: "contabilidad", label: "Contabilidad", emoji: "📊" },
  { value: "admin_llaves", label: "Admin Llaves", emoji: "🔑" },
];

const FormularioUsuario = () => {
  const navigate = useNavigate();
  const { user } = useParams();
  const location = useLocation();
  const modo = location.pathname.includes("/editar") ? "editar" : "crear";
  const { user: authUser } = useAuth();
  const rolActual = authUser?.tipo || "";
  // OJO: no usar "?? []" aqui. El valor null (superadmin) es un
  // sentinel a proposito ("todos los tipos"), y ?? lo confunde con "clave
  // inexistente" y lo convierte en [] (sin opciones).
  const tiposPermitidos = rolActual in TIPOS_PERMITIDOS_POR_ROL
    ? TIPOS_PERMITIDOS_POR_ROL[rolActual]
    : [];
  const opcionesTipo = tiposPermitidos === null
    ? TODOS_LOS_TIPOS
    : TODOS_LOS_TIPOS.filter((t) => tiposPermitidos.includes(t.value));

  const [form, setForm] = useState({
    user: "",
    nombre: "",
    apellidos: "",
    telefono: "",
    correo: "",
    tipo: "",
    id_empleado: "",
  });
  const [originalUser, setOriginalUser] = useState("");

  // Verificación id_empleado
  const [checkEmpleado, setCheckEmpleado] = useState(null); // null | { existe, nombre, piso } | 'loading'
  const checkTimerRef = React.useRef(null);

  // Crear: campos contraseña
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Editar: reset contraseña
  const [mostrarReset, setMostrarReset] = useState(false);
  const [nuevaPassword, setNuevaPassword] = useState("");
  const [confirmarNueva, setConfirmarNueva] = useState("");
  const [showNueva, setShowNueva] = useState(false);
  const [showConfirmarNueva, setShowConfirmarNueva] = useState(false);
  const [formMsg, setFormMsg] = useState(null); // { tipo: 'error'|'ok', texto }
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (modo === "editar" && user) {
      axios
        .get(`/api/usuarios/${user}`)
        .then((res) => {
          const payload = res.data;
          if (payload?.success && payload.data) {
            setForm(payload.data);
            setOriginalUser(payload.data.user);
          } else if (payload && !payload.success) {
            alert(payload.error || "Usuario no encontrado");
            navigate(-1);
          } else {
            if (payload && payload.user) {
              setForm(payload);
              setOriginalUser(payload.user);
            } else {
              alert("Usuario no encontrado");
              navigate(-1);
            }
          }
        })
        .catch(() => {
          alert("Error al cargar el usuario");
          navigate(-1);
        });
    }
  }, [modo, user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "id_empleado") {
      setCheckEmpleado(null);
      clearTimeout(checkTimerRef.current);
      const v = value.trim();
      if (v && /^\d+$/.test(v)) {
        setCheckEmpleado('loading');
        checkTimerRef.current = setTimeout(async () => {
          try {
            const res = await axios.get(`/api/usuarios/check_id_empleado/${v}`);
            setCheckEmpleado(res.data);
          } catch { setCheckEmpleado({ existe: false }); }
        }, 500);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMsg(null);

    if (modo === "crear") {
      if (!form.user || !password || !form.tipo) {
        setFormMsg({ tipo: 'error', texto: "Los campos 'Usuario', 'Contraseña' y 'Tipo' son obligatorios." });
        return;
      }
      if (password !== confirmPassword) {
        setFormMsg({ tipo: 'error', texto: "Las contraseñas no coinciden." });
        return;
      }
    } else {
      if (!form.user || !form.tipo) {
        setFormMsg({ tipo: 'error', texto: "Los campos 'Usuario' y 'Tipo' son obligatorios." });
        return;
      }
      if (mostrarReset) {
        if (!nuevaPassword) {
          setFormMsg({ tipo: 'error', texto: 'Introduce la nueva contraseña.' });
          return;
        }
        if (nuevaPassword !== confirmarNueva) {
          setFormMsg({ tipo: 'error', texto: 'Las contraseñas no coinciden.' });
          return;
        }
      }
    }

    setGuardando(true);
    try {
      if (modo === "crear") {
        const res = await axios.post(`/api/usuarios`, { ...form, password });
        if (!res.data.success) throw new Error(res.data.error || "Error creando usuario");
        setFormMsg({ tipo: 'ok', texto: 'Usuario creado correctamente.' });
        setTimeout(() => volverTrasGuardar(navigate, "/admin/usuarios", `✅ Usuario "${form.user}" creado correctamente.`), 900);
      } else {
        const payload = { ...form };
        if (mostrarReset && nuevaPassword) payload.password = nuevaPassword;
        const res = await axios.put(`/api/usuarios/${originalUser}`, payload);
        if (!res.data.success) throw new Error(res.data.error || "Error actualizando usuario");
        setFormMsg({ tipo: 'ok', texto: 'Usuario actualizado correctamente.' });
        setTimeout(() => volverTrasGuardar(navigate, "/admin/usuarios", `✅ Usuario "${form.user}" actualizado correctamente.`), 900);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Error inesperado.";
      setFormMsg({ tipo: 'error', texto: msg });
      setGuardando(false);
    }
  };

  const tipoElegido = TODOS_LOS_TIPOS.find((t) => t.value === form.tipo);

  return (
    <div style={styles.page}>
      <style>{`@keyframes girar { to { transform: rotate(360deg); } }`}</style>
      {guardando && (
        <div style={styles.overlayCargando}>
          <div style={styles.spinnerBox}>
            <div style={styles.spinner} />
            <div style={{ marginTop: 10, fontWeight: 700, color: "#1e293b" }}>Guardando…</div>
          </div>
        </div>
      )}
      <div style={styles.container}>
        <div style={styles.header}>
          <button type="button" onClick={() => navigate(-1)} style={styles.btnVolver}>← Volver</button>
          <h2 style={styles.title}>{modo === "crear" ? "➕ Crear Usuario" : "✏️ Editar Usuario"}</h2>
        </div>

        {formMsg && (
          <div style={{ ...stylesMsg.box, ...(formMsg.tipo === 'ok' ? stylesMsg.ok : stylesMsg.err) }}>
            {formMsg.tipo === 'ok' ? '✅ ' : '⚠️ '}{formMsg.texto}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>

          <section style={styles.section}>
            <div style={styles.sectionTitle}>Datos de acceso</div>

            <label style={styles.label}>Usuario*</label>
            <input style={styles.input} type="text" name="user" value={form.user} onChange={handleChange} required />

            {/* ── CONTRASEÑA MODO CREAR ── */}
            {modo === "crear" && (<>
              <label style={styles.label}>Contraseña*</label>
              <div style={styles.pwRow}>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ ...styles.input, ...styles.pwInput }}
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowPassword(v => !v)} style={styles.eyeBtn}
                  title={showPassword ? "Ocultar" : "Mostrar"}>
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>

              <label style={styles.label}>Confirmar contraseña*</label>
              <div style={styles.pwRow}>
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  style={{ ...styles.input, ...styles.pwInput }}
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowConfirm(v => !v)} style={styles.eyeBtn}
                  title={showConfirm ? "Ocultar" : "Mostrar"}>
                  {showConfirm ? "🙈" : "👁"}
                </button>
              </div>
              {confirmPassword && (
                <div style={password !== confirmPassword ? stylesMsg.fieldErr : stylesMsg.fieldOk}>
                  {password !== confirmPassword ? '✕ Las contraseñas no coinciden' : '✔ Las contraseñas coinciden'}
                </div>
              )}
            </>)}

            {/* ── CONTRASEÑA MODO EDITAR ── */}
            {modo === "editar" && (
              <div style={{ margin: "4px 0 0" }}>
                <button
                  type="button"
                  onClick={() => { setMostrarReset(v => !v); setNuevaPassword(""); setConfirmarNueva(""); setFormMsg(null); }}
                  style={styles.keyBtn}
                  title="Restablecer contraseña"
                >
                  <span style={{ fontSize: 15 }}>🔑</span>
                  <span>{mostrarReset ? "Cancelar restablecimiento" : "Restablecer contraseña"}</span>
                </button>
              </div>
            )}

            {modo === "editar" && mostrarReset && (
              <div style={styles.resetBox}>
                <label style={styles.label}>Nueva contraseña*</label>
                <div style={styles.pwRow}>
                  <input
                    type={showNueva ? "text" : "password"}
                    value={nuevaPassword}
                    onChange={e => { setNuevaPassword(e.target.value); setFormMsg(null); }}
                    style={{ ...styles.input, ...styles.pwInput }}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowNueva(v => !v)} style={styles.eyeBtn}
                    title={showNueva ? "Ocultar" : "Mostrar"}>
                    {showNueva ? "🙈" : "👁"}
                  </button>
                </div>

                <label style={styles.label}>Confirmar contraseña*</label>
                <div style={styles.pwRow}>
                  <input
                    type={showConfirmarNueva ? "text" : "password"}
                    value={confirmarNueva}
                    onChange={e => { setConfirmarNueva(e.target.value); setFormMsg(null); }}
                    style={{
                      ...styles.input, ...styles.pwInput,
                      borderColor: confirmarNueva && nuevaPassword !== confirmarNueva ? '#dc3545' : confirmarNueva && nuevaPassword === confirmarNueva ? '#28a745' : undefined,
                    }}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirmarNueva(v => !v)} style={styles.eyeBtn}
                    title={showConfirmarNueva ? "Ocultar" : "Mostrar"}>
                    {showConfirmarNueva ? "🙈" : "👁"}
                  </button>
                </div>
                {confirmarNueva && (
                  <div style={nuevaPassword !== confirmarNueva ? stylesMsg.fieldErr : stylesMsg.fieldOk}>
                    {nuevaPassword !== confirmarNueva ? '✕ Las contraseñas no coinciden' : '✔ Las contraseñas coinciden'}
                  </div>
                )}
              </div>
            )}
          </section>

          <section style={styles.section}>
            <div style={styles.sectionTitle}>Datos personales</div>

            <label style={styles.label}>Nombre</label>
            <input style={styles.input} type="text" name="nombre" value={form.nombre || ""} onChange={handleChange} />

            <label style={styles.label}>Apellidos</label>
            <input style={styles.input} type="text" name="apellidos" value={form.apellidos || ""} onChange={handleChange} />

            <label style={styles.label}>Teléfono</label>
            <input
              style={styles.input}
              type="tel"
              name="telefono"
              value={form.telefono || ""}
              onChange={handleChange}
              pattern="[0-9]{9}"
              placeholder="9 dígitos"
            />

            <label style={styles.label}>Correo</label>
            <input
              style={styles.input}
              type="email"
              name="correo"
              value={form.correo || ""}
              onChange={handleChange}
              placeholder="nombre@campushome.es"
              autoComplete="off"
            />
          </section>

          <section style={styles.section}>
            <div style={styles.sectionTitle}>Rol</div>

            <label style={styles.label}>Tipo*</label>
            <select style={styles.input} name="tipo" value={form.tipo} onChange={handleChange} required>
              <option value="">-- Seleccionar --</option>
              {opcionesTipo.map((t) => (
                <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>
              ))}
            </select>
            {tipoElegido && (
              <div style={styles.tipoBadge}>{tipoElegido.emoji} {tipoElegido.label}</div>
            )}

            {/* ── ID EMPLEADO (todos los tipos) ── */}
            <label style={{ ...styles.label, marginTop: 12 }}>ID Empleado</label>
            <input
              style={styles.input}
              type="number"
              name="id_empleado"
              value={form.id_empleado || ""}
              onChange={handleChange}
              placeholder="Número de empleado (opcional)"
            />
            {/* La verificación contra pisos solo aplica a asistentas (id_asistenta en pisos) */}
            {form.tipo === "asistenta" && (
              <>
                {checkEmpleado === 'loading' && (
                  <div style={stylesCheck.loading}>⏳ Comprobando...</div>
                )}
                {checkEmpleado && checkEmpleado !== 'loading' && checkEmpleado.existe && (
                  <div style={stylesCheck.ok}>
                    ✅ ¿Crear usuario para <strong>{checkEmpleado.nombre}</strong>?<br />
                    <span style={{ fontSize: 12 }}>Pisos: {checkEmpleado.pisos.join(" · ")}</span>
                  </div>
                )}
                {checkEmpleado && checkEmpleado !== 'loading' && !checkEmpleado.existe && form.id_empleado && (
                  <div style={stylesCheck.err}>
                    ❌ Este ID no existe todavía en ningún piso. Puedes crear el usuario igualmente.
                  </div>
                )}
              </>
            )}
          </section>

          <div style={styles.actions}>
            <button type="submit" style={{ ...styles.btnGuardar, opacity: guardando ? 0.7 : 1 }} disabled={guardando}>
              {guardando ? "Guardando…" : modo === "crear" ? "➕ Crear" : "💾 Guardar Cambios"}
            </button>
            <button type="button" onClick={() => navigate(-1)} style={styles.btnCancelar}>Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg,#eef2ff,#f8fafc)",
    padding: "32px 16px",
    fontFamily: "Segoe UI, sans-serif",
  },
  container: {
    maxWidth: "540px",
    margin: "0 auto",
    padding: "26px 28px 30px",
    backgroundColor: "#fff",
    borderRadius: "18px",
    boxShadow: "0 12px 36px rgba(30,41,59,0.12)",
  },
  overlayCargando: {
    position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
  },
  spinnerBox: {
    background: "#fff", borderRadius: 16, padding: "28px 36px",
    display: "flex", flexDirection: "column", alignItems: "center",
    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
  },
  spinner: {
    width: 38, height: 38, border: "4px solid #e2e8f0", borderTopColor: "#4338ca",
    borderRadius: "50%", animation: "girar 0.8s linear infinite",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    marginBottom: 18,
  },
  btnVolver: {
    background: "#eef2ff",
    border: "none",
    borderRadius: 10,
    color: "#4338ca",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    padding: "8px 14px",
    whiteSpace: "nowrap",
  },
  title: {
    margin: 0,
    fontSize: 21,
    fontWeight: 800,
    color: "#1e293b",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "18px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    padding: "16px 18px",
  },
  sectionTitle: {
    fontSize: 11.5,
    fontWeight: 800,
    color: "#6366f1",
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginBottom: 2,
  },
  label: {
    fontSize: 13,
    fontWeight: 700,
    color: "#475569",
  },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box",
    background: "#fff",
  },
  tipoBadge: {
    alignSelf: "flex-start",
    marginTop: 2,
    fontSize: 12.5,
    fontWeight: 700,
    color: "#4338ca",
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: 20,
    padding: "4px 12px",
  },
  pwRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  pwInput: {
    flex: 1,
  },
  eyeBtn: {
    background: "#fff",
    border: "1.5px solid #e2e8f0",
    borderRadius: 10,
    cursor: "pointer",
    padding: "8px 12px",
    fontSize: 16,
    lineHeight: 1,
  },
  keyBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    background: "#fff8e1", border: "1px solid #ffc107", borderRadius: 10,
    cursor: "pointer", padding: "8px 14px", fontSize: 13, color: "#856404", fontWeight: 700,
  },
  resetBox: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#fff3cd",
    border: "1px solid #ffc107",
    borderRadius: 10,
    padding: "12px 14px",
    marginTop: 4,
  },
  actions: {
    marginTop: 4,
    display: "flex",
    gap: "10px",
  },
  btnGuardar: {
    flex: 1,
    padding: "13px 20px",
    borderRadius: 12,
    border: "none",
    background: "linear-gradient(135deg,#6366f1,#4338ca)",
    color: "#fff",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 4px 14px rgba(79,70,229,0.35)",
  },
  btnCancelar: {
    padding: "13px 20px",
    borderRadius: 12,
    border: "1.5px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    fontWeight: 700,
    fontSize: 15,
    cursor: "pointer",
  },
};

export default FormularioUsuario;

const stylesMsg = {
  box:      { borderRadius: 10, padding: "12px 16px", fontSize: 13.5, marginBottom: 4, fontWeight: 600 },
  ok:       { background: "#d1e7dd", color: "#0a3622", border: "1px solid #a3cfbb" },
  err:      { background: "#f8d7da", color: "#58151c", border: "1px solid #f1aeb5" },
  fieldErr: { fontSize: 12, color: "#dc3545", marginTop: 2, marginBottom: 2 },
  fieldOk:  { fontSize: 12, color: "#28a745", marginTop: 2, marginBottom: 2 },
};

const stylesCheck = {
  loading: { fontSize: 13, color: "#6b7280", padding: "8px 12px", background: "#f3f4f6", borderRadius: 8, marginTop: 4 },
  ok:  { fontSize: 13, color: "#065f46", padding: "8px 12px", background: "#d1fae5", border: "1px solid #6ee7b7", borderRadius: 8, lineHeight: 1.5, marginTop: 4 },
  err: { fontSize: 13, color: "#991b1b", padding: "8px 12px", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, lineHeight: 1.5, marginTop: 4 },
};
